import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
} from "react";
import { List, useDynamicRowHeight, useListCallbackRef } from "react-window";
import { useRecoilValue } from "recoil";

import { useLayoutViewportHeight, usePageContext, useRtl } from "@ryde/redux";
import {
  buildGroupedTableRowsLayoutSignature,
  buildGroupValuesLayoutSignature,
  DEFAULT_GROUPED_TABLE_HEIGHT,
  draggingBetweenRidesGridIdSelector,
  IExtendableDto,
  TABLE_WRAPPER_DEFAULT_OVERSCAN_COUNT,
  TABLE_WRAPPER_DRAG_OVERSCAN_COUNT,
  tableRowsComputedSF,
  useScheduleDynamicRowHeightUpdate,
} from "@ryde/shared";

import { TableWrapperWindowRow } from "./table-wrapper-window-row";
import { TableWrapperContext } from "./table-wrapper.context";

/** Passenger server grouping: load more group values when list is scrolled near the end. */
const GROUP_VALUES_SCROLL_END_OFFSET_PX = 200;

interface ITableWrapperVariableSizeListProps {
  groupValues: IExtendableDto[keyof IExtendableDto][] | undefined[];
  setOuterElement: Dispatch<SetStateAction<HTMLDivElement | null>>;
  onScrollEnd?: () => void;
}

export const TableWrapperVariableSizeList: FC<
  ITableWrapperVariableSizeListProps
> = ({ groupValues, setOuterElement, onScrollEnd }) => {
  const { entity } = usePageContext();
  const { groupingField } = useContext(TableWrapperContext);
  const { direction } = useRtl();
  const isDragging = useRecoilValue(draggingBetweenRidesGridIdSelector);
  const tableRows = useRecoilValue(tableRowsComputedSF(entity));
  const listHeight = useLayoutViewportHeight();
  const [listApi, setListApi] = useListCallbackRef();

  const groupedTableLayoutSignature = buildGroupedTableRowsLayoutSignature({
    tableRows,
    groupingField,
  });

  const groupValuesSignature = buildGroupValuesLayoutSignature(groupValues);

  const rowCount = groupValues.length;

  const groupedTableResetSignature = `${groupValuesSignature}|${rowCount}|${groupedTableLayoutSignature}`;

  const dynamicRowHeight = useDynamicRowHeight({
    defaultRowHeight: DEFAULT_GROUPED_TABLE_HEIGHT,
    key: groupedTableResetSignature,
  });

  const scheduleRowHeightUpdate =
    useScheduleDynamicRowHeightUpdate(dynamicRowHeight);

  const setSize = useCallback(
    (index: number, measuredHeight: number) => {
      const previousHeight = dynamicRowHeight.getRowHeight(index);
      if (previousHeight === measuredHeight) {
        return;
      }

      scheduleRowHeightUpdate(index, measuredHeight);
    },
    [dynamicRowHeight, scheduleRowHeightUpdate],
  );

  const rowProps = useMemo(
    () => ({
      groupValues,
      setSize,
      layoutSignature: groupedTableLayoutSignature,
    }),
    // groupValues read from closure when signature changes (same ordered group keys).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupValuesSignature, groupedTableLayoutSignature, setSize],
  );

  useLayoutEffect(() => {
    setOuterElement(listApi?.element ?? null);
  }, [listApi, setOuterElement]);

  const handleScroll = useCallback(() => {
    const scrollElement = listApi?.element;
    if (!scrollElement || !onScrollEnd) {
      return;
    }

    const { scrollHeight, clientHeight, scrollTop } = scrollElement;
    if (
      scrollTop + clientHeight >=
      scrollHeight - GROUP_VALUES_SCROLL_END_OFFSET_PX
    ) {
      onScrollEnd();
    }
  }, [listApi, onScrollEnd]);

  return (
    <List
      listRef={setListApi}
      rowComponent={TableWrapperWindowRow}
      rowCount={rowCount}
      rowHeight={dynamicRowHeight}
      rowProps={rowProps}
      dir={direction}
      className="middle_scrollbar"
      overscanCount={
        isDragging
          ? TABLE_WRAPPER_DRAG_OVERSCAN_COUNT
          : TABLE_WRAPPER_DEFAULT_OVERSCAN_COUNT
      }
      style={{ height: listHeight, width: "100%" }}
      onScroll={onScrollEnd ? handleScroll : undefined}
    />
  );
};
